import { formatDate } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { dateOfBirthComponentLabels } from 'src/app/app.models';
import { stateOrCountryDropdownAnimation } from 'src/app/shared/reusable-animations/card-estimate-drawer-animations';

export interface Option {
  name?: string;
  label?: string;
  value?: any;
}

@Component({
    selector: 'app-dob-dropdown',
    templateUrl: './dob-dropdown.component.html',
    styleUrls: ['./dob-dropdown.component.scss'],
    animations: [stateOrCountryDropdownAnimation],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DobDropdownComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class DobDropdownComponent implements ControlValueAccessor {
  date: any = new Date();
  currentYear = new Date().getFullYear();

  @Input() isDOBValid: boolean = false;

  get Date(): any {
    return this.date;
  }
  @Input()
  set Date(value: any) {
    if (value === this.date) return;
    this.date = value;
    this.onChange(this.date);
    this.DateChange.emit(this.date);
    this.setDate();
  }

  @Output()
  public DateChange: EventEmitter<Date> = new EventEmitter<Date>();

  allDates: number[] = [];
  dates: number[] = [];
  months: any = [];
  years: number[] = [];
  thirtyDaysMonths: string[] = ['Apr', 'Jun', 'Sep', 'Nov'];
  enMonths: string[] = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  selectedDate: number;
  selectedMonth: number | any;
  selectedYear: number;
  private _labels: any;

  // Dropdown state management
  showMonthDropdown: boolean = false;
  showDayDropdown: boolean = false;
  showYearDropdown: boolean = false;

  @ViewChildren('monthContainer, dayContainer, yearContainer')
  dropdownContainers: QueryList<ElementRef>;

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const targetElement = event.target as HTMLElement;
    if (this.dropdownContainers) {
      let clickedInside = false;
      this.dropdownContainers.forEach((container) => {
        if (container.nativeElement.contains(targetElement)) {
          clickedInside = true;
        }
      });
      if (!clickedInside) {
        this.closeAllDropdowns();
      }
    }
  }

  @Input() public set labels(labels: dateOfBirthComponentLabels) {
    this._labels = labels;
    this.months = [];
    for (let [key, value] of Object.entries(this.labels.months)) {
      this.months.push(value);
    }
    this.setDate();
  }

  public get labels() {
    return this._labels;
  }

  constructor(private cdRef: ChangeDetectorRef) {
    for (let i = 1; i <= 31; i++) {
      this.allDates.push(i);
    }
    for (let i = this.currentYear - 100; i <= this.currentYear - 18; i++) {
      this.years.push(i);
    }

    this.years.reverse();
    this.dates = this.allDates;
  }

  onChange: any = () => {};
  onTouch: any = () => {};
  val = null;

  set controlValue(val: any) {
    this.val = val;
    this.Date = val;
    this.onChange(val);
    this.onTouch(val);
  }

  writeValue(value: any) {
    this.controlValue = value;
    this.Date = value;
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouch = fn;
  }

  setDate() {
    if (!this.isDOBValid && !this.date) {
      this.selectedMonth = 0;
      this.selectedDate = 0;
      this.selectedYear = 0;
    } else {
      const date: any = this.date;
      if (date === 'Invalid Date') {
        // console.log('Date is Invalid');
        this.selectedDate = 0;
        this.selectedMonth = 0;
        this.selectedYear = 0;
      } else if (this.date && date != 'Invalid Date') {
        const formatedDate = formatDate(this.date, 'yyyy-MM-dd', 'en');
        const [, year, month, day] =
          formatedDate.toString().match(/^(\d{4})-(\d{2})-(\d{2})/) || [];
        this.selectedDate = +day;
        // this.selectedMonth = Object.values(this.labels.months)[+month - 1];
        this.selectedMonth = this.months[+month - 1];
        this.selectedYear = +year;
      }
    }
    this.setDates();
  }

  getMonthNumber(name: string) {
    return this.months.findIndex((month: string) => month === name) + 1;
  }

  isLeapYear() {
    return (
      this.selectedYear % 400 === 0 ||
      (this.selectedYear % 100 !== 0 && this.selectedYear % 4 === 0)
    );
  }

  dateChanged(value: any) {
    let monthNumber = this.getMonthNumber(this.selectedMonth);
    if (this.selectedYear && this.selectedMonth && value) {
      if (this.selectedDate > 0) {
        if (monthNumber < 10) {
          monthNumber = `${'0' + monthNumber}`;
        }
        if (value < 10) {
          value = `${'0' + value}`;
        }
        this.Date = `${this.selectedYear}-${monthNumber}-${value}`;
      }
    }
  }

  setDates() {
    let maxDate = 31;
    // getting the month name in english from months data
    const monthIndex = this.months.findIndex(
      (mon: string) => mon === this.selectedMonth
    );
    const monthVal = monthIndex
      ? this.enMonths[monthIndex]
      : this.selectedMonth;

    if (this.selectedMonth === 2 || this.selectedMonth === this.months[1]) {
      maxDate = this.isLeapYear() ? 29 : 28;
    } else if (this.thirtyDaysMonths.includes(monthVal)) {
      maxDate = 30;
    }
    this.dates = this.allDates.filter((f) => f <= maxDate);
  }

  onValueChange(month: string, year: number) {
    let monthNumber = this.getMonthNumber(this.selectedMonth);
    // getting the month name in english from months data
    const monthIndex = this.months.findIndex((mon: string) => mon === month);
    const monthVal = monthIndex ? this.enMonths[monthIndex] : month;
    let maxDate = 31;
    if (monthNumber === 2) {
      maxDate = this.isLeapYear() ? 29 : 28;
    } else if (this.thirtyDaysMonths.includes(monthVal)) {
      maxDate = 30;
    }
    this.dates = this.allDates.filter((f) => f <= maxDate);
    let dayValue = '';
    if (this.selectedDate > maxDate) {
      this.Date = new Date('');
      this.selectedDate = 0;
      this.cdRef.detectChanges();
    } else if (year && month && this.selectedDate) {
      if (this.selectedDate > 0) {
        if (monthNumber < 10) {
          monthNumber = `${'0' + monthNumber}`;
        }
        if (this.selectedDate < 10) {
          dayValue = `${'0' + this.selectedDate}`;
        } else {
          dayValue = `${this.selectedDate}`;
        }
        this.Date = `${year}-${monthNumber}-${dayValue}`;
      } else {
        this.Date = '';
      }
    }
  }

  monthChanged(name: string) {
    this.onValueChange(name, this.selectedYear);
  }

  yearChanged(value: any) {
    this.onValueChange(this.selectedMonth, value);
  }

  // New dropdown methods for Tailwind implementation
  toggleDropdown(type: 'month' | 'day' | 'year') {
    this.closeAllDropdowns();
    switch (type) {
      case 'month':
        this.showMonthDropdown = true;
        break;
      case 'day':
        this.showDayDropdown = true;
        break;
      case 'year':
        this.showYearDropdown = true;
        break;
    }
  }

  closeAllDropdowns() {
    this.showMonthDropdown = false;
    this.showDayDropdown = false;
    this.showYearDropdown = false;
  }

  selectMonth(month: any) {
    this.selectedMonth = month;
    this.monthChanged(month);
    this.showMonthDropdown = false;
  }

  selectDay(day: number) {
    this.selectedDate = day;
    this.dateChanged(day);
    this.showDayDropdown = false;
  }

  selectYear(year: number) {
    this.selectedYear = year;
    this.yearChanged(year);
    this.showYearDropdown = false;
  }
}
